import React from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import type { WarnedPro } from '../utils/dragFillWarnings';

interface DragFillWarningModalProps {
  /** Non-empty list of PROs with warnings. If empty, nothing renders. */
  warnedPros: WarnedPro[];
  /** Total number of cells the user is about to fill (across all PROs). */
  totalCells: number;
  /** Called when the user accepts; the parent should proceed with the fill. */
  onConfirm: () => void;
  /** Called when the user cancels; the parent should skip the fill. */
  onCancel: () => void;
}

/**
 * Single batched confirm dialog for the drag-to-fill flow. Lists every PRO
 * that triggered any of Drop Release / Not Receiving / Not Railbilled /
 * Missing Preadvise, with the specific warnings under each. One
 * Continue/Cancel for the whole fill.
 */
export const DragFillWarningModal: React.FC<DragFillWarningModalProps> = ({
  warnedPros,
  totalCells,
  onConfirm,
  onCancel,
}) => {
  // Alt+C cancels the drag-fill. Confirm gets no chord — it writes the dragged
  // value onto every warned PRO. The claim is gated on the same emptiness check
  // as the render below, so the hook stays above the early return.
  useAltHotkeys({ C: onCancel }, undefined, { layer: warnedPros.length > 0 });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(warnedPros.length > 0);

  if (warnedPros.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-scrim/50" onClick={onCancel} />
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative bg-panel rounded-lg shadow-xl w-full max-w-md mx-4"
      >
        <ModalHeader title="Dispatch Warnings" onClose={onCancel} dragHandleProps={dragHandleProps} />
        <div className="p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          You are about to assign a driver to {totalCells} cell{totalCells === 1 ? '' : 's'}.{' '}
          {warnedPros.length} PRO{warnedPros.length === 1 ? '' : 's'} ha
          {warnedPros.length === 1 ? 's' : 've'} warnings:
        </p>
        <ul className="text-sm text-label mb-4 max-h-60 overflow-y-auto space-y-2">
          {warnedPros.map((p) => (
            <li key={p.pronumber}>
              <span className="font-medium">PRO #{p.pronumber}</span>
              <ul className="ml-4 list-disc list-inside text-gray-600 dark:text-gray-400">
                {p.messages.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Assign Anyway
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DragFillWarningModal;
